import { mmToPx, paperDimsMm } from "./paper.js";
import type { ExportRequest, PageDef, PageRenderer, RenderedPage } from "./types.js";

/**
 * Render every page of an {@link ExportRequest} to PNG bytes using the
 * supplied surface-specific renderer. Returns one {@link RenderedPage} per
 * input page, in the same order.
 */
export async function renderRequestToPngs<TSnap>(
  snapshot: TSnap,
  request: ExportRequest,
  renderer: PageRenderer<TSnap>,
): Promise<RenderedPage[]> {
  const ratio = request.pixelRatio ?? 2;
  const out: RenderedPage[] = [];
  for (const page of request.pages) {
    const { wMm, hMm } = paperDimsMm(page.paper, page.orientation);
    const pixelW = mmToPx(wMm, ratio);
    const pixelH = mmToPx(hMm, ratio);
    const canvas = document.createElement("canvas");
    await renderer.draw({ snapshot, page, canvas, pixelW, pixelH });
    const pngBytes = await canvasToPngBytes(canvas);
    out.push({ page, pngBytes, pixelW, pixelH });
  }
  return out;
}

/** Render a single page synchronously into a data URL. */
export async function renderPageToDataUrl<TSnap>(
  snapshot: TSnap,
  page: PageDef,
  renderer: PageRenderer<TSnap>,
  pixelRatio = 2,
): Promise<string> {
  const { wMm, hMm } = paperDimsMm(page.paper, page.orientation);
  const pixelW = mmToPx(wMm, pixelRatio);
  const pixelH = mmToPx(hMm, pixelRatio);
  const canvas = document.createElement("canvas");
  await renderer.draw({ snapshot, page, canvas, pixelW, pixelH });
  return canvas.toDataURL("image/png");
}

async function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))), "image/png");
  });
  return new Uint8Array(await blob.arrayBuffer());
}
